import React, { useEffect, useRef } from 'react';
import { HighlightWithPlayer } from '../../types/highlights.types';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import '../../styles/components/highlights.scss';

const getTagDisplay = (tag: string): string => {
  const tagMap: { [key: string]: string } = {
    'OneK': '1K',
    'TwoK': '2K',
    'ThreeK': '3K',
    'FourK': '4K',
    'Ace': 'ACE',
  };
  return tagMap[tag] || tag;
};

interface HighlightPlayerProps {
  highlight: HighlightWithPlayer;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

const HighlightPlayer: React.FC<HighlightPlayerProps> = ({
  highlight,
  onClose,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Auto-play when modal opens
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Error auto-playing video:', error);
      });
    }

    // Handle escape key to close
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Handle arrow keys for navigation
    const handleKeyNavigation = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        onPrevious();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleKeyNavigation);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleKeyNavigation);
    };
  }, [onClose, onNext, onPrevious, hasNext, hasPrevious]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="highlight-player" onClick={handleBackdropClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleBackdropClick(e as any); }}>
      <div className="highlight-player__container">
        <button
          className="highlight-player__close"
          onClick={onClose}
          aria-label="Close player"
        >
          <FaTimes />
        </button>

        {hasPrevious && onPrevious && (
          <button
            className="highlight-player__nav highlight-player__nav--prev"
            onClick={onPrevious}
            aria-label="Previous clip"
          >
            <FaChevronLeft />
          </button>
        )}

        {hasNext && onNext && (
          <button
            className="highlight-player__nav highlight-player__nav--next"
            onClick={onNext}
            aria-label="Next clip"
          >
            <FaChevronRight />
          </button>
        )}

        <div className="highlight-player__video-wrapper">
          <video
            ref={videoRef}
            className="highlight-player__video"
            src={highlight.clipUrl}
            controls
            autoPlay
            loop
          />
        </div>

        <div className="highlight-player__info">
          <div className="highlight-player__info-left">
            <span className="highlight-player__player-name">
              {highlight.playerName || 'Unknown Player'}
            </span>
            <span className="highlight-player__round">
              Round {highlight.roundNumber}
            </span>
          </div>
          <div className="highlight-player__info-right">
            {highlight.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="highlight-player__tag">
                {getTagDisplay(tag)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HighlightPlayer;
